import * as React from 'react';
import * as intl from 'react-intl-universal';
import { Alert } from 'antd';
import Editor from './Editor';
import { languages } from '../../i18n';
import uploadUtils from '../../utils/upload';
import Loading from '../Loading';
import helper, { FileType } from './utils';
import CommonApi from '../../../pc-sales/api.v2/common';

interface RichTextEditorProps {
    value?: string;
    onChange?: Function;
    onBlur?: Function;
    tips?: string;
    extension?: Function;
    imgOnCall?: Function;
    menu?: string;
    isRelativeUrl?: boolean;
    imgMaxWidth?: number;
    convertUrls?: boolean;
    disabled?: boolean;
}

interface RichTextEditorState {
    isLoading: boolean;
    value: string;
    floating: boolean;
}

interface BlobInfo {
    name: () => string;
    blob: () => Blob;
    base64: () => string;
    uri: () => string;
    blobUri: () => string;
    filename: () => string;
}

interface IEditor {
    getContent: Function;
    setContent: Function;
    addButton: Function;
    selection: {
        setContent: Function;
    };
    contentWindow: {
        focus: Function;
    };
}

const MAX_ALLOWED_IMG_SIZE_30MB = 1024 * 1024 * 30;
const SUPPORT_FILE_TYPES: FileType[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

const getLanguagePackUrl = () => {
    const lang = languages.get();
    return ['zh-CN', 'zh-TW', 'ko-KR'].includes(lang) ? window.config.js_file(`/editor_${lang}.js`) : null;
};

const uploadImg = (
    blobInfo: BlobInfo,
    isRelativeUrl: boolean,
    successCallback: (fileURL: string) => void,
    failureCallback: (errorMsg: string) => void,
) => {
    try {
        if (blobInfo.blob().size > MAX_ALLOWED_IMG_SIZE_30MB) {
            failureCallback(uploadUtils.getLocales(languages.get()).size);
            return;
        }
        const fileReader = new FileReader();
        fileReader.onload = (e: ProgressEvent<FileReader>) => {
            uploadUtils.imagePress({ src: e.target.result, quality: 0.3 }, async (file: Blob) => {
                const res = await CommonApi.upload({ file });
                successCallback(isRelativeUrl ? res.url.aws : res.url.oss);
            });
        };
        fileReader.readAsDataURL(blobInfo.blob());
    } catch (err) {
        failureCallback(intl.get('Validation.UploadFailed'));
    }
};

// TODO: refactor it to a function component
export default class RichText extends React.Component<RichTextEditorProps, RichTextEditorState> {
    private mounted: boolean;
    private editor: IEditor;
    private defaultMenu =
        'formatselect | styleselect | browse | undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | forecolor backcolor | image link | table | code';

    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: true,
            floating: true,
            value: '',
        };
    }

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    UNSAFE_componentWillReceiveProps({ value }: RichTextEditorProps) {
        if (this.props.value !== value) this.mounted && this.setState({ value });
    }

    handleEditorChange = (value: string) =>
        this.mounted &&
        this.setState({ value }, () => {
            const { onChange } = this.props;
            onChange && onChange(value);
        });

    handleFileUpload = () => {
        const { isRelativeUrl } = this.props;
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', helper.getFileTypes(SUPPORT_FILE_TYPES));
        input.onchange = async () => {
            const file = input.files[0];
            if (!file || this.state.isLoading || !this.editor) return;
            if (!helper.isAcceptable(file, SUPPORT_FILE_TYPES)) return;
            this.mounted && this.setState({ isLoading: true });

            try {
                const res = await CommonApi.upload({ file });
                const elm = document.createElement('a');
                elm.href = isRelativeUrl ? res.url.aws : res.url.oss;
                elm.innerText = file.name;
                elm.rel = 'noopener';
                elm.target = '_blank';
                this.editor.selection.setContent(elm.outerHTML);
                this.mounted && this.setState({ isLoading: false });
            } catch (err) {
                this.mounted && this.setState({ isLoading: false });
            }
        };
        input.click();
    };

    /**
     * 初始化控制
     */
    initHandler = (ed: IEditor) => {
        const { value = '', extension } = this.props;
        this.editor = ed;
        extension && extension(ed);
        this.mounted && this.setState({ isLoading: false, value });
    };

    initFileUploader = (ed: IEditor) => {
        ed.addButton('browse', {
            title: intl.get('Component.UploadFile'),
            icon: 'browse',
            onclick: this.handleFileUpload,
        });
    };

    /**
     * 增加蒙版承接antd trigger事件
     * @param {boolean} floating
     * @returns {boolean | any}
     */
    floatingHandler = (floating: boolean) => {
        if (this.props.disabled) return;
        if (!floating) {
            const ed = this.editor as any;
            ed.delegates.focusin();
            ed.contentWindow.focus();
        }
        if (floating && this.props.onBlur) this.props.onBlur();
        this.mounted && this.setState({ floating });
    };

    handleImgUpload = (
        blobInfo: BlobInfo,
        successCallback: (fileURL: string) => void,
        failureCallback: (errorMsg: string) => void,
    ) => {
        const { imgOnCall, isRelativeUrl } = this.props;
        this.setState({ isLoading: true });
        imgOnCall && imgOnCall(true);
        uploadImg(
            blobInfo,
            isRelativeUrl,
            (data: any) => {
                this.setState({ isLoading: false });
                imgOnCall && imgOnCall(false);
                successCallback(data);
            },
            failureCallback,
        );
    };

    RenderMenu = (menu: string, defaultMenu: string) => {
        if (menu) {
            return menu;
        }
        return defaultMenu;
    };

    render() {
        const { value, isLoading, floating } = this.state;
        const { tips, menu, convertUrls = true, disabled, imgMaxWidth } = this.props;
        return (
            <Loading enabled={isLoading}>
                {tips && <Alert message={tips} showIcon />}
                <div
                    className="rich-text-float"
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        backgroundColor: disabled ? 'rgba(233, 233, 233, 0.45)' : undefined,
                        display: floating ? 'block' : 'none',
                        zIndex: 5,
                    }}
                    onClick={() => this.floatingHandler(false)}
                />
                <Editor
                    value={value}
                    imgMaxWidth={imgMaxWidth}
                    init={{
                        baseURI: window.config.other_file('/vender/'),
                        plugins: 'link textcolor image lists colorpicker code paste table',
                        toolbar: this.RenderMenu(menu, this.defaultMenu),
                        paste_data_images: true,
                        min_height: 200,
                        min_width: 720,
                        language_url: getLanguagePackUrl(),
                        file_picker_types: 'file image',
                        theme: 'modern',
                        branding: false,
                        menubar: false,
                        style_formats: [
                            { title: intl.get('Editor.FirstIndent'), block: 'p', styles: { textIndent: '30px' } },
                            {
                                title: intl.get('Editor.PictureAdapt'),
                                selector: 'img',
                                styles: { minWidth: '100%', minHeight: '100%', verticalAlign: 'middle' },
                            },
                            {
                                title: intl.get('Editor.Font'),
                                items: [
                                    { title: '8pt', inline: 'span', styles: { fontSize: '8px' } },
                                    { title: '10pt', inline: 'span', styles: { fontSize: '10px' } },
                                    { title: '12pt', inline: 'span', styles: { fontSize: '12px' } },
                                    { title: '14pt', inline: 'span', styles: { fontSize: '14px' } },
                                    { title: '16pt', inline: 'span', styles: { fontSize: '16px' } },
                                    { title: '18pt', inline: 'span', styles: { fontSize: '18px' } },
                                    { title: '20pt', inline: 'span', styles: { fontSize: '20px' } },
                                    { title: '24pt', inline: 'span', styles: { fontSize: '24px' } },
                                    { title: '36pt', inline: 'span', styles: { fontSize: '36px' } },
                                ],
                            },
                            {
                                title: intl.get('Editor.Title'),
                                items: [
                                    { title: `${intl.get('Editor.Title')}`, block: 'p' },
                                    { title: `${intl.get('Editor.Title')}1`, block: 'h1' },
                                    { title: `${intl.get('Editor.Title')}2`, block: 'h2' },
                                    { title: `${intl.get('Editor.Title')}3`, block: 'h3' },
                                    { title: `${intl.get('Editor.Title')}4`, block: 'h4' },
                                    { title: `${intl.get('Editor.Title')}5`, block: 'h5' },
                                    { title: `${intl.get('Editor.Title')}6`, block: 'h6' },
                                ],
                            },
                            {
                                title: intl.get('Editor.LineSpace'),
                                block: 'p',
                                items: [
                                    { title: '1.0', block: 'p', styles: { lineHeight: '1' } },
                                    { title: '1.5', block: 'p', styles: { lineHeight: '1.5' } },
                                    { title: '2.0', block: 'p', styles: { lineHeight: '2' } },
                                    { title: '2.5', block: 'p', styles: { lineHeight: '2.5' } },
                                    { title: '3.0', block: 'p', styles: { lineHeight: '3' } },
                                ],
                            },
                            {
                                title: intl.get('Editor.RowHeight'),
                                block: 'p',
                                items: [
                                    { title: '8pt', block: 'p', styles: { lineHeight: '8px' } },
                                    { title: '10pt', block: 'p', styles: { lineHeight: '10px' } },
                                    { title: '12pt', block: 'p', styles: { lineHeight: '12px' } },
                                    { title: '14pt', block: 'p', styles: { lineHeight: '14px' } },
                                    { title: '16pt', block: 'p', styles: { lineHeight: '16px' } },
                                    { title: '18pt', block: 'p', styles: { lineHeight: '18px' } },
                                    { title: '20pt', block: 'p', styles: { lineHeight: '20px' } },
                                    { title: '24pt', block: 'p', styles: { lineHeight: '24px' } },
                                    { title: '36pt', block: 'p', styles: { lineHeight: '36px' } },
                                ],
                            },
                            {
                                title: intl.get('Editor.PictureMargin'),
                                items: [
                                    { title: '5px', selector: 'img', styles: { margin: '5px' } },
                                    { title: '10px', selector: 'img', styles: { margin: '10px' } },
                                    { title: '15px', selector: 'img', styles: { margin: '15px' } },
                                    { title: '24px', selector: 'img', styles: { margin: '24px' } },
                                    { title: '36px', selector: 'img', styles: { margin: '36px' } },
                                ],
                            },
                        ],
                        relative_urls: false,
                        convert_urls: convertUrls,
                        images_upload_handler: this.handleImgUpload,
                        init_instance_callback: this.initHandler,
                        setup: this.initFileUploader,
                        content_style: 'li{display: list-item;} p{margin-bottom:0;margin-top:0;}',
                    }}
                    onEditorChange={this.handleEditorChange}
                    onFloating={this.floatingHandler}
                />
            </Loading>
        );
    }
}
